// import { Helmet } from "react-helmet-async";
// import { serializeJsonLd } from "./jsonLd";
// import { versionedImageUrl, stampFrom } from "./ogImageVersion";

// const SITE_NAME = "Zetech University";
// const SITE_URL = "https://www.zetech.ac.ke";
// const DEFAULT_IMAGE = `${SITE_URL}/logo512.png`;
// const TWITTER_HANDLE = "@zetechuniv";
// const DEFAULT_KEYWORDS = "...";

// /**
//  * Build the single canonical URL used by <link rel="canonical">, og:url and
//  * every JSON-LD @id/url on the page. Normalising here (instead of at each
//  * call site) guarantees the rendered canonical and the structured-data URLs
//  * never drift apart — crawlers treat a mismatch as two different pages.
//  *
//  * Rules: absolute URLs are respected, relative paths get a leading slash,
//  * query strings and hashes are dropped, and trailing slashes are removed
//  * (except for the site root, which stays "https://www.zetech.ac.ke/").
//  */
// export function buildCanonicalUrl(path = "", canonicalOverride) {
//   const raw = canonicalOverride || path || "/";

//   let url;
//   if (/^https?:\/\//i.test(raw)) {
//     url = raw;
//   } else {
//     const cleaned = raw.startsWith("/") ? raw : `/${raw}`;
//     url = `${SITE_URL}${cleaned}`;
//   }

//   // Drop query + hash — they never belong in a canonical URL.
//   url = url.split("#")[0].split("?")[0];

//   // Collapse duplicate slashes in the path portion only.
//   url = url.replace(/([^:]\/)\/+/g, "$1");

//   // Strip the trailing slash unless the URL is just the origin.
//   if (url.length > SITE_URL.length + 1 && url.endsWith("/")) {
//     url = url.replace(/\/+$/, "");
//   }
//   if (url === SITE_URL) url = `${SITE_URL}/`;

//   return url;
// }

// /**
//  * Drop-in per-page SEO tags (title, meta description, canonical,
//  * Open Graph, Twitter Card, optional JSON-LD structured data).
//  * Every routed page should render one of these with a unique
//  * title/description — search engines otherwise see every page sharing
//  * the single static title/description from public/index.html, which
//  * reads as duplicate content.
//  *
//  * `path` is the route's path (e.g. "/programmes" or `/programme/${slug}`)
//  * used to build the canonical + og:url. Pass `noIndex` for pages that
//  * should never appear in search results (admin, not-found, etc).
//  *
//  * `breadcrumbs` — [{ name, path }] — emits a BreadcrumbList so Google
//  * can render the breadcrumb trail in results instead of a bare URL.
//  * `jsonLd` — a single schema object or an array of them (Course,
//  * Article, Event, FAQPage…) appended after the breadcrumb graph.
//  */
// export default function Seo({
//   title,
//   rawTitle,
//   description,
//   path = "",
//   canonical: canonicalOverride,
//   image = DEFAULT_IMAGE,
//   imageAlt,
//   keywords,
//   type = "website",
//   noIndex = false,
//   breadcrumbs,
//   jsonLd,
//   webPageType = "WebPage",
//   datePublished,
//   dateModified,
//   includeWebPage = true,
// }) {
//   const fullTitle = rawTitle || (title ? `${title} | ${SITE_NAME}` : SITE_NAME);
//   // Single source of truth: the same string feeds <link rel="canonical">,
//   // og:url and every JSON-LD @id/url below.
//   const canonical = buildCanonicalUrl(path, canonicalOverride);
//   // Social crawlers and schema validators both reject relative image paths,
//   // so anything passed in as "/foo.jpg" is resolved against the site origin.
//   const resolvedImage = /^https?:\/\//i.test(String(image || ""))
//     ? image
//     : `${SITE_URL}${String(image || "").startsWith("/") ? "" : "/"}${image || ""}`;
//   // Versioned so Facebook/LinkedIn/X re-scrape instead of serving the
//   // preview they cached before the artwork was replaced.
//   const absoluteImage = versionedImageUrl(
//     resolvedImage,
//     stampFrom(dateModified || datePublished)
//   );

//   const schemas = [];

//   // Per-page WebPage node: ties this exact URL to its own name/description
//   // and points mainEntityOfPage back at itself, so crawlers attribute the
//   // page's metadata to the page rather than falling back to the sitewide
//   // WebSite/Organization graph. isPartOf/publisher link it to those nodes.
//   if (includeWebPage) {
//     const webPage = {
//       "@context": "https://schema.org",
//       "@type": webPageType,
//       "@id": `${canonical}#webpage`,
//       url: canonical,
//       name: fullTitle,
//       inLanguage: "en-KE",
//       mainEntityOfPage: { "@type": "WebPage", "@id": `${canonical}#webpage` },
//       isPartOf: { "@id": `${SITE_URL}/#website` },
//       publisher: { "@id": `${SITE_URL}/#organization` },
//       primaryImageOfPage: {
//         "@type": "ImageObject",
//         url: absoluteImage,
//         caption: imageAlt || fullTitle,
//       },
//     };

//     if (description) webPage.description = description;
//     if (keywords) webPage.keywords = keywords;
//     if (datePublished) webPage.datePublished = datePublished;
//     if (dateModified || datePublished) {
//       webPage.dateModified = dateModified || datePublished;
//     }
//     if (breadcrumbs?.length) {
//       webPage.breadcrumb = { "@id": `${canonical}#breadcrumb` };
//     }

//     schemas.push(webPage);
//   }

//   if (breadcrumbs?.length) {
//     schemas.push({
//       "@context": "https://schema.org",
//       "@type": "BreadcrumbList",
//       "@id": `${canonical}#breadcrumb`,
//       itemListElement: breadcrumbs.map((crumb, index) => ({
//         "@type": "ListItem",
//         position: index + 1,
//         name: crumb.name,
//         item: buildCanonicalUrl(crumb.path || "/"),
//       })),
//     });
//   }

//   if (jsonLd) {
//     (Array.isArray(jsonLd) ? jsonLd : [jsonLd]).forEach((entry) => {
//       if (entry) {
//         schemas.push({
//           "@context": "https://schema.org",
//           ...entry,
//           ...(entry.mainEntityOfPage
//             ? {}
//             : { mainEntityOfPage: { "@id": `${canonical}#webpage` } }),
//         });
//       }
//     });
//   }


//   return (
//     <Helmet prioritizeSeoTags>
//       <title>{fullTitle}</title>
//       {description && <meta name="description" content={description} />}
//       {keywords && <meta name="keywords" content={keywords} />}
//       <link rel="canonical" href={canonical} />
//       {/* Language/region targeting: tells Google this page serves the
//           Kenyan English audience (and is the default worldwide), which
//           strengthens local ranking signals for searches within Kenya. */}
//       {!noIndex && <link rel="alternate" hrefLang="en-KE" href={canonical} />}
//       {!noIndex && <link rel="alternate" hrefLang="x-default" href={canonical} />}
//       <meta
//         name="robots"
//         content={
//           noIndex
//             ? "noindex, nofollow"
//             : "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1"
//         }
//       />

//       <meta property="og:type" content={type} />
//       <meta property="og:site_name" content={SITE_NAME} />
//       <meta property="og:locale" content="en_KE" />
//       <meta property="og:title" content={fullTitle} />
//       {description && <meta property="og:description" content={description} />}
//       <meta property="og:url" content={canonical} />
//       <meta property="og:image" content={absoluteImage} />
//       <meta property="og:image:secure_url" content={absoluteImage} />
//       <meta property="og:image:type" content="image/jpeg" />
//       {/* Facebook/LinkedIn render the card without a re-fetch when the
//           dimensions are declared; 1200x630 is the shipped default card. */}
//       <meta property="og:image:width" content="1200" />
//       <meta property="og:image:height" content="630" />
//       <meta property="og:image:alt" content={imageAlt || fullTitle} />

//       <meta name="twitter:card" content="summary_large_image" />
//       <meta name="twitter:site" content={TWITTER_HANDLE} />
//       <meta name="twitter:title" content={fullTitle} />
//       {description && <meta name="twitter:description" content={description} />}
//       <meta name="twitter:image" content={absoluteImage} />
//       <meta name="twitter:image:alt" content={imageAlt || fullTitle} />

//       {schemas
//         .map((schema) => serializeJsonLd(schema))
//         .filter(Boolean)
//         .map((json, index) => (
//           <script type="application/ld+json" key={index}>
//             {json}
//           </script>
//         ))}
//     </Helmet>
//   );
// }




import { Helmet } from "react-helmet-async";
import { serializeJsonLd } from "./jsonLd";
import { versionedImageUrl, stampFrom } from "./ogImageVersion";

const SITE_NAME = "Zetech University";
const SITE_URL = "https://www.zetech.ac.ke";
const DEFAULT_IMAGE = `${SITE_URL}/logo512.png`;
const TWITTER_HANDLE = "@zetechuniv";
// Fallback keyword set for pages that don't pass their own.
const DEFAULT_KEYWORDS =
  "Zetech University, universities in Kenya, private university Kenya, degree courses Kenya, diploma courses Kenya, TVET Kenya, masters programmes Kenya, PhD Kenya, university admission Kenya";

/**
 * Canonical URL configuration
 *
 * Zetech's canonical URL policy:
 *
 * - HTTPS only
 * - www.zetech.ac.ke only
 * - Query strings removed
 * - Hash fragments removed
 * - Duplicate path slashes removed
 * - Trailing slash preserved for non-root routes
 * - Root remains https://www.zetech.ac.ke/
 *
 * Examples:
 *
 * /programmes
 *      -> https://www.zetech.ac.ke/programmes/
 *
 * /programmes/
 *      -> https://www.zetech.ac.ke/programmes/
 *
 * /programmes/?utm_source=google
 *      -> https://www.zetech.ac.ke/programmes/
 *
 * /programmes#degrees
 *      -> https://www.zetech.ac.ke/programmes/
 *
 * https://zetech.ac.ke/programmes
 *      -> https://www.zetech.ac.ke/programmes/
 */
export function buildCanonicalUrl(path = "", canonicalOverride) {
  const raw = String(canonicalOverride || path || "/").trim();

  let url;

  try {
    /*
     * URL() safely handles both absolute and relative URLs.
     */
    url = new URL(raw, `${SITE_URL}/`);
  } catch {
    url = new URL("/", `${SITE_URL}/`);
  }

  /*
   * Always force the canonical hostname and protocol.
   *
   * This prevents:
   * http://zetech.ac.ke
   * https://zetech.ac.ke
   * http://www.zetech.ac.ke
   *
   * from becoming separate canonical identities.
   */
  url.protocol = "https:";
  url.hostname = "www.zetech.ac.ke";
  url.port = "";

  /*
   * Query strings and fragments do not belong in the canonical URL.
   */
  url.search = "";
  url.hash = "";

  /*
   * Normalize duplicate slashes in the PATH ONLY.
   *
   * We deliberately do not alter the protocol because
   * https:// contains two slashes by design.
   */
  let pathname = url.pathname || "/";

  pathname = pathname.replace(/\/{2,}/g, "/");

  /*
   * Root URL must remain:
   *
   * https://www.zetech.ac.ke/
   */
  if (pathname === "") {
    pathname = "/";
  }

  /*
   * Zetech canonical URL convention:
   *
   * /about/ -> /about
   * /programmes/ -> /programmes
   *
   * This must match the sitemap and redirect strategy.
   */
  if (pathname !== "/") {
    pathname = pathname.replace(/\/+$/, "") || "/";
  }

  url.pathname = pathname;

  return url.toString();
}

/**
 * Resolve an image into an absolute HTTPS URL.
 */
function resolveImageUrl(image) {
  const raw = String(image || DEFAULT_IMAGE).trim();

  try {
    const url = new URL(raw, `${SITE_URL}/`);

    url.protocol = "https:";

    return url.toString();
  } catch {
    return DEFAULT_IMAGE;
  }
}

/**
 * Return an appropriate MIME type based on the image extension.
 */
function getImageMimeType(imageUrl) {
  const pathname = new URL(imageUrl).pathname.toLowerCase();

  if (pathname.endsWith(".png")) {
    return "image/png";
  }

  if (pathname.endsWith(".webp")) {
    return "image/webp";
  }

  if (pathname.endsWith(".avif")) {
    return "image/avif";
  }

  return "image/jpeg";
}

/**
 * Per-page SEO component.
 *
 * Every indexable route should provide:
 *
 * - unique title
 * - unique description
 * - route path
 * - optional image
 * - optional breadcrumbs
 * - optional structured data
 *
 * Use noIndex for:
 *
 * - admin pages
 * - login pages
 * - private dashboards
 * - search results
 * - 404 pages
 * - temporary/internal routes
 */
export default function Seo({
  title,
  rawTitle,
  description,
  path = "",
  canonical: canonicalOverride,
  image = DEFAULT_IMAGE,
  imageAlt,
  keywords,
  type = "website",
  noIndex = false,
  breadcrumbs,
  jsonLd,
  webPageType = "WebPage",
  datePublished,
  dateModified,
  includeWebPage = true,
}) {
  /*
   * Build one canonical URL and reuse it everywhere.
   *
   * canonical
   * og:url
   * WebPage @id
   * WebPage URL
   * Breadcrumb references
   *
   * all come from the same value.
   */
  const canonical = buildCanonicalUrl(
    path,
    canonicalOverride
  );

  const fullTitle =
    rawTitle ||
    (title
      ? `${title} | ${SITE_NAME}`
      : SITE_NAME);

  /*
   * Resolve social image.
   */
  const resolvedImage = resolveImageUrl(image);

  /*
   * Add a cache-busting version when the page has a
   * publication/modification date.
   */
  const absoluteImage = versionedImageUrl(
    resolvedImage,
    stampFrom(dateModified || datePublished)
  );

  const imageType = getImageMimeType(resolvedImage);

  const schemas = [];

  /*
   * ==========================================================
   * WEBPAGE SCHEMA
   * ==========================================================
   */
  if (includeWebPage && !noIndex) {
    const webPage = {
      "@context": "https://schema.org",
      "@type": webPageType,
      "@id": `${canonical}#webpage`,
      url: canonical,
      name: fullTitle,
      inLanguage: "en-KE",

      isPartOf: {
        "@id": `${SITE_URL}/#website`,
      },

      publisher: {
        "@id": `${SITE_URL}/#organization`,
      },

      // Speakable tells voice assistants which parts of the page are worth
      // reading aloud — an AEO signal for "hey Google, tell me about…".
      speakable: {
        "@type": "SpeakableSpecification",
        cssSelector: ["h1", "p"],
      },
      primaryImageOfPage: {
        "@type": "ImageObject",
        url: absoluteImage,
        caption: imageAlt || fullTitle,
      },
    };

    if (description) {
      webPage.description = description;
    }

    if (keywords) {
      webPage.keywords = keywords;
    }

    if (datePublished) {
      webPage.datePublished = datePublished;
    }

    if (dateModified || datePublished) {
      webPage.dateModified =
        dateModified || datePublished;
    }

    if (breadcrumbs?.length) {
      webPage.breadcrumb = {
        "@id": `${canonical}#breadcrumb`,
      };
    }

    schemas.push(webPage);
  }

  /*
   * ==========================================================
   * BREADCRUMBS
   * ==========================================================
   */
  if (breadcrumbs?.length && !noIndex) {
    schemas.push({
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "@id": `${canonical}#breadcrumb`,

      itemListElement: breadcrumbs.map(
        (crumb, index) => ({
          "@type": "ListItem",
          position: index + 1,
          name: crumb.name,
          item: buildCanonicalUrl(
            crumb.path || "/"
          ),
        })
      ),
    });
  }

  /*
   * ==========================================================
   * CUSTOM JSON-LD
   * ==========================================================
   */
  if (jsonLd && !noIndex) {
    const entries = Array.isArray(jsonLd)
      ? jsonLd
      : [jsonLd];

    entries.forEach((entry) => {
      if (!entry) return;

      schemas.push({
        "@context": "https://schema.org",
        ...entry,

        ...(entry.mainEntityOfPage
          ? {}
          : {
              mainEntityOfPage: {
                "@id": `${canonical}#webpage`,
              },
            }),
      });
    });
  }

  return (
    <Helmet prioritizeSeoTags>

      {/* =====================================================
          BASIC SEO
      ====================================================== */}

      <title>{fullTitle}</title>

      {description && (
        <meta
          name="description"
          content={description}
        />
      )}

      {/* Google ignores meta keywords, but Bing-adjacent engines and most
          third-party SEO auditors still read them, so every page ships a
          relevant set rather than leaving the field empty. */}
      <meta
        name="keywords"
        content={keywords || DEFAULT_KEYWORDS}
      />

      {/* Attribution: audit tools and AI answer engines look for a named
          publisher/author before trusting a page's claims. */}
      <meta name="author" content={SITE_NAME} />
      <meta name="publisher" content={SITE_NAME} />
      <meta name="copyright" content={SITE_NAME} />

      {/* =====================================================
          CANONICAL
      ======================================================

          Important:
          No canonical is emitted for noindex pages.
      */}

      {!noIndex && (
        <link
          rel="canonical"
          href={canonical}
        />
      )}

      {/* =====================================================
          ROBOTS
      ====================================================== */}

      <meta
        name="robots"
        content={
          noIndex
            ? "noindex, nofollow, noarchive"
            : "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1"
        }
      />

      {/* =====================================================
          HREFLANG
      ====================================================== */}

      {!noIndex && (
        <>
          <link
            rel="alternate"
            hrefLang="en-KE"
            href={canonical}
          />

          <link
            rel="alternate"
            hrefLang="x-default"
            href={canonical}
          />
        </>
      )}

      {/* =====================================================
          OPEN GRAPH
      ====================================================== */}

      <meta
        property="og:type"
        content={type}
      />

      <meta
        property="og:site_name"
        content={SITE_NAME}
      />

      <meta
        property="og:locale"
        content="en_KE"
      />

      <meta
        property="og:title"
        content={fullTitle}
      />

      {description && (
        <meta
          property="og:description"
          content={description}
        />
      )}

      <meta
        property="og:url"
        content={canonical}
      />

      <meta
        property="og:image"
        content={absoluteImage}
      />

      <meta
        property="og:image:secure_url"
        content={absoluteImage}
      />

      <meta
        property="og:image:type"
        content={imageType}
      />

      <meta
        property="og:image:width"
        content="1200"
      />

      <meta
        property="og:image:height"
        content="630"
      />

      <meta
        property="og:image:alt"
        content={imageAlt || fullTitle}
      />

      {/* =====================================================
          TWITTER / X
      ====================================================== */}

      <meta
        name="twitter:card"
        content="summary_large_image"
      />

      <meta
        name="twitter:site"
        content={TWITTER_HANDLE}
      />

      <meta
        name="twitter:title"
        content={fullTitle}
      />

      {description && (
        <meta
          name="twitter:description"
          content={description}
        />
      )}

      <meta
        name="twitter:image"
        content={absoluteImage}
      />

      <meta
        name="twitter:image:alt"
        content={imageAlt || fullTitle}
      />

      {/* =====================================================
          STRUCTURED DATA
      ====================================================== */}

      {schemas
        .map((schema) => serializeJsonLd(schema))
        .filter(Boolean)
        .map((json, index) => (
          <script
            type="application/ld+json"
            key={`seo-schema-${index}`}
          >
            {json}
          </script>
        ))}

    </Helmet>
  );
}
